
import { defineComponent, ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import StrategyTable from './subscription/StrategyTable.vue'
import StrategyInfo from './subscription/StrategyInfo.vue'
import YieldChart from './subscription/YieldChart.vue'
import PositionPanel from './subscription/PositionPanel.vue'
import LoadingMask from '@/components/LoadingMask.vue'
import type { StrategyItem } from './subscription/StrategyTable.vue'
import { zh_tran, objTran } from '@/utils/lang.js'
import strategyApi from '@/api/strategy'
import { message } from 'ant-design-vue'

// 接口返回的原始策略条目结构
interface RawStrategyItem {
  code: string
  name: string
  tags: string[]
  profile: string
  hasGroupPermission: boolean
  increaseDay1: number
  increaseDay3: number
  increaseDay5: number
  yearReturns: number
  threeMonthReturns: number
  maxDrawDown: number
  style: string
  holdPeriod: number
  dayReturns: { tradingDay: number; cumulativeReturn: number; totalReturn: number }[]
  benchmarkReturns: { tradingDay: number; cumulativeReturn: number; benchmarkCode: string; benchmarkName: string }[]
  stocks: {
    updateTime?: number
    total?: number
    stocks?: {
      market: string
      symbol: string
      name: string
      buyPrice: null | number
      buyTime: null | string
      lastPx: number
      pxChangeRate: number
    }[]
  }
}

// 将接口字段映射到 StrategyItem
function mapToStrategyItem(raw: RawStrategyItem): StrategyItem {
  // 从 benchmarkCode 推断市场：HSI → HK，SPX → US
  const benchmarkCode = raw.benchmarkReturns?.[0]?.benchmarkCode || ''
  const market = benchmarkCode === 'HSI' ? 'HK' : benchmarkCode === 'SPX' ? 'US' : 'HK'
  return {
    code: raw.code,
    name: raw.name,
    market,
    dayReturn: raw.increaseDay1,
    day3Return: raw.increaseDay3,
    day5Return: raw.increaseDay5,
    yearReturn: raw.yearReturns,
    month3Return: raw.threeMonthReturns,
    maxDrawDown: raw.maxDrawDown,
    holdPeriod: raw.holdPeriod,
    tags: raw.tags || [],
    profile: raw.profile || ''
  }
}


export default defineComponent({
  name: 'SubscriptionView',
  components: { StrategyTable, StrategyInfo, YieldChart, PositionPanel, LoadingMask },
  setup() {
    const route = useRoute()
    const isDark = computed(() => route.query.theme !== 'light')
    const selectedCode = ref('')
    const prefer = (route.query.preferGreenUptrendRedDowntrend as string) || ''

    // 语言切换：根据路由参数 Accept-Language 判断，zh-Hant 为繁体，默认 zh-Hans 为简体
    const langParam = (route.query['Accept-Language'] as string) || 'zh-Hans'
    const isTraditional = langParam === 'zh-Hant'

    // 初始化时设置语言模式：仅繁体才做转换
    if (isTraditional) {
      zh_tran('t')
    }

    const strategyList = ref<StrategyItem[]>([])
    // token 从路由参数获取
    const token = (route.query.token as string) || ''
    // 保存原始接口数据
    const rawList = ref<RawStrategyItem[]>([])
    const unsubscribeLoading = ref(false)
    const listLoading = ref(false)

    const selectedStrategy = computed(() =>
      strategyList.value.find(s => s.code === selectedCode.value) || null
    )

    const selectStrategy = (item: StrategyItem) => {
      selectedCode.value = item.code
    }

    // 删除订阅：调接口 status=0，成功后重新拉列表
    const handleUnsubscribe = async (item: StrategyItem) => {
      if (unsubscribeLoading.value) return
      unsubscribeLoading.value = true
      try {
        await strategyApi.favoriteStrategy({ token, code: item.code, status: 0 })
        message.success(isTraditional ? '刪除訂閱成功' : '删除订阅成功')
        await fetchStrategyList()
      } catch (e) {
        console.error('删除订阅失败', e)
        message.error(isTraditional ? '刪除訂閱失敗' : '删除订阅失败')
      } finally {
        unsubscribeLoading.value = false
      }
    }

    // 获取收藏策略列表（type 写死 favorites）
    const fetchStrategyList = async () => {
      listLoading.value = true
      try {
        const res: any = await strategyApi.getStrategyList({ token, type: 'favorites' })
        if (res?.data?.list?.length) {
          rawList.value = res.data.list as RawStrategyItem[]

          const mapped = rawList.value.map(mapToStrategyItem)
          strategyList.value = isTraditional
            ? objTran('t', mapped) as StrategyItem[]
            : mapped

          // 保持当前选中；若当前选中已不在列表中（被删除），则选第一个
          const stillExists = strategyList.value.some(s => s.code === selectedCode.value)
          if (!stillExists) {
            selectedCode.value = strategyList.value[0].code
          }
        } else {
          rawList.value = []
          strategyList.value = []
          selectedCode.value = ''
        }
      } catch (e) {
        console.error('获取收藏策略列表失败', e)
      } finally {
        listLoading.value = false
        if (isTraditional) nextTick(() => zh_tran('t'))
      }
    }

    // 主题变量
    const themeVars = computed(() => {
      if (isDark.value) {
        return {
          '--sub-bg': '#111424',
          '--panel-bg': '#111424',
          '--panel-border': 'rgba(255,255,255,0.06)',
          '--header-bg': '#181c26',
          '--header-text': '#ffffff',
          '--text-primary': 'rgba(255,255,255,0.85)',
          '--text-secondary': 'rgba(255,255,255,0.45)',
          '--row-hover': 'rgba(255,255,255,0.04)',
          '--row-active': 'rgba(74,158,255,0.08)',
          '--tag-border': '#ff4d6d',
          '--tag-color': '#ff4d6d',
          '--btn-bg': '#4a9eff',
          '--btn-text': '#fff',
          '--gap-bg': '#000000'
        }
      }
      return {
        '--sub-bg': '#ffffff',
        '--panel-bg': '#ffffff',
        '--panel-border': '#e8e8e8',
        '--header-bg': '#f0f3fb',
        '--header-text': '#1d1d1d',
        '--text-primary': '#1d1d1d',
        '--text-secondary': '#7d7d7d',
        '--row-hover': '#f5f7fa',
        '--row-active': 'rgba(74,158,255,0.06)',
        '--tag-border': '#ff4d6d',
        '--tag-color': '#ff4d6d',
        '--btn-bg': '#4a9eff',
        '--btn-text': '#fff',
        '--gap-bg': '#E6E9EF'
      }
    })

    onMounted(() => {
      fetchStrategyList()
    })

    return {
      isDark,
      isTraditional,
      prefer,
      strategyList,
      selectedCode,
      selectedStrategy,
      selectStrategy,
      handleUnsubscribe,
      unsubscribeLoading,
      listLoading,
      themeVars
    }
  }
})
